/**
 * Salary Income working paper — AXIORA (Feb 2026)
 *
 * Route: /tax/salary
 * One row per employer. Engine auto-computes HRA exemption (Sec 10(13A)),
 * aggregates gross taxable salary + professional tax + TDS across all rows,
 * and pushes into the ITR computation. Standard deduction (Sec 16(ia)) is
 * applied by the main engine at compute time based on regime.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft, Send, X, Check, Briefcase, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { describeAxiosError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const currentAY = () => {
  const d = new Date();
  const fyStart = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${fyStart + 1}-${String((fyStart + 2) % 100).padStart(2, "0")}`;
};

const EMPTY_ROW = () => ({
  employer_name: "",
  employer_tan: "",
  basic: 0, da: 0,
  hra_received: 0, rent_paid: 0, is_metro: false,
  special_allowances_taxable: 0, bonus_commission: 0,
  lta_taxable: 0, gratuity_taxable: 0, leave_encashment_taxable: 0,
  commuted_pension_taxable: 0, uncommuted_pension: 0,
  perquisites: 0, profits_in_lieu: 0,
  other_taxable: 0, other_exempt: 0,
  professional_tax_paid: 0, tds_by_employer: 0,
  include_in_computation: true, notes: "",
});

function Inner() {
  const { activeId } = useCompany();
  const nav = useNavigate();
  const [ay, setAy] = useState(currentAY());
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pushOpen, setPushOpen] = useState(false);
  const [computations, setComputations] = useState([]);
  const [selectedCompId, setSelectedCompId] = useState("");

  const refresh = async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const r = await api.get(`/companies/${activeId}/salary/summary`, { params: { ay } });
      setRows(r.data.rows || []);
      setSummary(r.data);
    } catch (err) {
      toast.error(describeAxiosError(err));
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [activeId, ay]);

  const openNew  = () => { setEditing({ ...EMPTY_ROW() }); setDialogOpen(true); };
  const openEdit = (r) => { setEditing({ ...r }); setDialogOpen(true); };
  const save = async () => {
    try {
      const payload = { ...editing, ay };
      if (editing.id) {
        await api.patch(`/companies/${activeId}/salary/rows/${editing.id}`, payload);
      } else {
        await api.post(`/companies/${activeId}/salary/rows`, payload);
      }
      toast.success("Saved");
      setDialogOpen(false);
      refresh();
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.employer_name || "row"}"?`)) return;
    try {
      await api.delete(`/companies/${activeId}/salary/rows/${r.id}`);
      toast.success("Deleted");
      refresh();
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };
  const openPushDialog = async () => {
    setPushOpen(true);
    try {
      const r = await api.get(`/companies/${activeId}/tax/computations`);
      setComputations(r.data || []);
      setSelectedCompId(r.data?.[0]?.id || "");
    } catch (err) { toast.error(describeAxiosError(err)); }
  };
  const pushToITR = async () => {
    if (!selectedCompId) return;
    try {
      const r = await api.post(
        `/companies/${activeId}/salary/push-to-itr/${selectedCompId}`,
        null, { params: { ay } },
      );
      const t = r.data.summary;
      toast.success(`Pushed gross ₹${formatINR(t.included_gross)}, prof-tax ₹${formatINR(t.included_prof_tax_allowed_old)} — TDS by employer ₹${formatINR(t.included_tds)} (add via TDS Ledger)`);
      setPushOpen(false);
    } catch (err) { toast.error(describeAxiosError(err)); }
  };

  const totals = summary?.totals || { gross: 0, hra_exempt: 0, prof_tax_paid: 0, prof_tax_allowed_old: 0, tds_by_employer: 0, included_gross: 0, total_rows: 0 };
  const perRow = summary?.per_row || [];

  return (
    <>
      <PageHeader
        title="Salary Income · Working Paper"
        subtitle="Sections 15-17. One row per employer, unlimited employers. HRA exemption auto-computed."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => nav("/tax")} className="h-8" data-testid="btn-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <select value={ay} onChange={(e) => setAy(e.target.value)} className="h-8 px-2 border border-gray-300 rounded text-xs" data-testid="select-ay">
              {["2024-25", "2025-26", "2026-27", "2027-28"].map((a) => <option key={a} value={a}>AY {a}</option>)}
            </select>
            <Button onClick={openPushDialog} variant="secondary" className="h-8" disabled={!totals.included_gross} data-testid="btn-push-itr">
              <Send className="w-4 h-4 mr-1" /> Push to ITR
            </Button>
            <Button onClick={openNew} className="h-8 bg-sky-700 hover:bg-sky-800" data-testid="btn-new-row">
              <Plus className="w-4 h-4 mr-1" /> Add employer
            </Button>
          </div>
        }
      />

      <div className="p-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="salary-kpis">
          <Kpi label="Gross Salary (sum)" value={totals.gross} tone="sky" testid="kpi-gross" />
          <Kpi label="HRA Exemption (Sec 10(13A))" value={totals.hra_exempt} tone="rose" testid="kpi-hra" />
          <Kpi label="Prof-Tax (Old regime cap)" value={totals.prof_tax_allowed_old} tone="gray" testid="kpi-proftax" />
          <Kpi label="TDS by Employer" value={totals.tds_by_employer} tone="blue" testid="kpi-tds" />
        </div>

        <div className="border border-sky-200 bg-sky-50 rounded p-3 text-xs text-sky-900 flex gap-2" data-testid="rule-hint">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>Standard Deduction Sec 16(ia)</strong> — ₹75,000 (New) / ₹50,000 (Old) — is applied automatically by the main tax engine when this data is pushed. <strong>Prof-tax</strong> is deductible only under Old regime, capped at ₹2,500. <strong>TDS-by-employer</strong> is shown here but is NOT auto-added to the ITR — add rows to the TDS Ledger inside your computation editor to keep an audit trail.
          </div>
        </div>

        {/* Rows */}
        <div className="border border-gray-200 bg-white rounded overflow-hidden" data-testid="rows-table">
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 text-xs font-medium text-gray-700 flex items-center justify-between">
            <span>Employers — AY {ay}</span>
            {loading && <span className="text-gray-400">Loading…</span>}
          </div>
          {rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500 flex flex-col items-center gap-2">
              <Briefcase className="w-10 h-10 text-gray-300" />
              No salary rows yet for AY {ay}.
            </div>
          ) : (
            <table className="tx-table">
              <thead>
                <tr>
                  <th>Employer</th>
                  <th className="text-right">Basic+DA</th>
                  <th className="text-right">HRA received</th>
                  <th className="text-right">HRA exempt</th>
                  <th className="text-right">Gross taxable</th>
                  <th className="text-right">Prof-tax</th>
                  <th className="text-right">TDS</th>
                  <th>Included?</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const meta = perRow.find((p) => p.id === r.id) || {};
                  return (
                    <tr key={r.id} data-testid={`row-${i}`}>
                      <td className="text-xs">
                        <div className="font-medium">{r.employer_name || "—"}</div>
                        {r.employer_tan && <div className="text-[10px] text-gray-500 font-mono">{r.employer_tan}</div>}
                      </td>
                      <td className="tx-num">{formatINR((r.basic || 0) + (r.da || 0))}</td>
                      <td className="tx-num">{formatINR(r.hra_received)}</td>
                      <td className="tx-num text-emerald-700">{formatINR(meta.hra_exemption?.exempt || 0)}</td>
                      <td className="tx-num font-medium">{formatINR(meta.gross_taxable_salary || 0)}</td>
                      <td className="tx-num">{formatINR(r.professional_tax_paid)}</td>
                      <td className="tx-num">{formatINR(r.tds_by_employer)}</td>
                      <td className="text-xs">{r.include_in_computation ? "✓" : "—"}</td>
                      <td className="whitespace-nowrap text-right">
                        <button onClick={() => openEdit(r)} className="text-blue-700 hover:underline mr-2" title="Edit" data-testid={`btn-edit-${i}`}>
                          <Pencil className="w-3.5 h-3.5 inline" />
                        </button>
                        <button onClick={() => del(r)} className="text-red-600 hover:text-red-800" title="Delete" data-testid={`btn-delete-${i}`}>
                          <Trash2 className="w-3.5 h-3.5 inline" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="row-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit" : "Add"} Employer Row</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Employer name" value={editing.employer_name} onChange={(v) => setEditing({ ...editing, employer_name: v })} testid="input-employer-name" />
                <Field label="Employer TAN" value={editing.employer_tan} onChange={(v) => setEditing({ ...editing, employer_tan: v })} testid="input-employer-tan" />
              </div>

              <SubSection title="Basic components">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <FieldNum label="Basic" value={editing.basic} onChange={(v) => setEditing({ ...editing, basic: v })} testid="input-basic" />
                  <FieldNum label="Dearness Allowance" value={editing.da} onChange={(v) => setEditing({ ...editing, da: v })} testid="input-da" />
                  <FieldNum label="Special allowances (taxable)" value={editing.special_allowances_taxable} onChange={(v) => setEditing({ ...editing, special_allowances_taxable: v })} />
                  <FieldNum label="Bonus / commission" value={editing.bonus_commission} onChange={(v) => setEditing({ ...editing, bonus_commission: v })} />
                </div>
              </SubSection>

              <SubSection title="HRA & Rent — Sec 10(13A)">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <FieldNum label="HRA received" value={editing.hra_received} onChange={(v) => setEditing({ ...editing, hra_received: v })} testid="input-hra" />
                  <FieldNum label="Rent paid" value={editing.rent_paid} onChange={(v) => setEditing({ ...editing, rent_paid: v })} testid="input-rent" />
                  <label className="flex items-end gap-2 text-xs pb-2">
                    <input type="checkbox" checked={editing.is_metro} onChange={(e) => setEditing({ ...editing, is_metro: e.target.checked })} data-testid="check-metro" />
                    Metro city (50% cap)
                  </label>
                </div>
                <p className="text-[10px] text-gray-500 mt-1">
                  Exemption = least of (actual HRA, rent paid − 10% of Basic+DA, 50% metro / 40% non-metro of Basic+DA).
                </p>
              </SubSection>

              <SubSection title="Retirement & one-time (enter TAXABLE amount after Sec 10 exemption)">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <FieldNum label="LTA taxable" value={editing.lta_taxable} onChange={(v) => setEditing({ ...editing, lta_taxable: v })} />
                  <FieldNum label="Gratuity taxable" value={editing.gratuity_taxable} onChange={(v) => setEditing({ ...editing, gratuity_taxable: v })} />
                  <FieldNum label="Leave encashment taxable" value={editing.leave_encashment_taxable} onChange={(v) => setEditing({ ...editing, leave_encashment_taxable: v })} />
                  <FieldNum label="Commuted pension taxable" value={editing.commuted_pension_taxable} onChange={(v) => setEditing({ ...editing, commuted_pension_taxable: v })} />
                  <FieldNum label="Uncommuted pension" value={editing.uncommuted_pension} onChange={(v) => setEditing({ ...editing, uncommuted_pension: v })} />
                </div>
                <p className="text-[10px] text-gray-500 mt-1">
                  Sec 10(10) caps gratuity at ₹20L, Sec 10(10AA) caps leave encashment at ₹25L, Sec 10(10A) covers pension. Enter the amount after exemption; see the Act for the exact caps.
                </p>
              </SubSection>

              <SubSection title="Perquisites & Profits in Lieu (Sec 17(2) & 17(3))">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <FieldNum label="Perquisites (aggregate)" value={editing.perquisites} onChange={(v) => setEditing({ ...editing, perquisites: v })} testid="input-perquisites" />
                  <FieldNum label="Profits in lieu of salary" value={editing.profits_in_lieu} onChange={(v) => setEditing({ ...editing, profits_in_lieu: v })} />
                  <FieldNum label="Other taxable" value={editing.other_taxable} onChange={(v) => setEditing({ ...editing, other_taxable: v })} />
                </div>
              </SubSection>

              <SubSection title="Deductions & pre-payments">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <FieldNum label="Professional tax paid (Sec 16(iii))" value={editing.professional_tax_paid} onChange={(v) => setEditing({ ...editing, professional_tax_paid: v })} testid="input-proftax" />
                  <FieldNum label="TDS by employer (Sec 192)" value={editing.tds_by_employer} onChange={(v) => setEditing({ ...editing, tds_by_employer: v })} testid="input-tds-by-employer" />
                </div>
              </SubSection>

              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={editing.include_in_computation} onChange={(e) => setEditing({ ...editing, include_in_computation: e.target.checked })} data-testid="check-include" />
                Include in ITR push
              </label>
              <Field label="Notes" value={editing.notes} onChange={(v) => setEditing({ ...editing, notes: v })} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="btn-cancel"><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={save} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-save"><Check className="w-4 h-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Push to ITR */}
      <Dialog open={pushOpen} onOpenChange={setPushOpen}>
        <DialogContent data-testid="push-dialog">
          <DialogHeader>
            <DialogTitle>Push salary to ITR</DialogTitle>
          </DialogHeader>
          {computations.length === 0 ? (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
              No saved computations found. <a href="/tax/computation/new" className="underline">Create one first</a> for AY {ay}.
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-xs">Choose the target computation</Label>
              <select value={selectedCompId} onChange={(e) => setSelectedCompId(e.target.value)} className="w-full h-9 px-2 border border-gray-300 rounded text-xs" data-testid="select-target-comp">
                {computations.map((c) => (
                  <option key={c.id} value={c.id}>{c.ay} — {c.profile_snapshot?.name || c.profile_snapshot?.taxpayer_type}</option>
                ))}
              </select>
              <p className="text-[11px] text-gray-500">
                Pushing gross ₹{formatINR(totals.included_gross)} → <code>salary.auto</code> + prof-tax ₹{formatINR(totals.included_prof_tax_allowed_old)} → <code>salary_professional_tax</code>. Standard deduction is applied by the engine automatically.
              </p>
              <p className="text-[11px] text-amber-700">
                TDS by employer (₹{formatINR(totals.included_tds)}) is NOT auto-pushed — add rows to the TDS Ledger inside the computation to keep an audit trail.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPushOpen(false)}>Cancel</Button>
            <Button onClick={pushToITR} disabled={!selectedCompId} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-confirm-push">
              <Send className="w-4 h-4 mr-1" /> Push
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* helpers */
function Kpi({ label, value, tone, testid }) {
  const bg = { sky: "bg-sky-50", rose: "bg-rose-50", gray: "bg-gray-50", blue: "bg-blue-50" }[tone];
  const fg = { sky: "text-sky-900", rose: "text-rose-900", gray: "text-gray-900", blue: "text-blue-900" }[tone];
  return (
    <div className={`border border-gray-200 ${bg} rounded p-3`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-gray-600">{label}</div>
      <div className={`text-lg font-medium ${fg}`}>₹ {formatINR(value)}</div>
    </div>
  );
}
function SubSection({ title, children }) {
  return (
    <div className="border border-gray-200 rounded p-3 bg-gray-50/40">
      <div className="text-[11px] uppercase tracking-wider text-gray-600 mb-2">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-gray-600">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9 text-xs" data-testid={testid} />
    </div>
  );
}
function FieldNum({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-gray-600">{label}</Label>
      <Input type="number" value={value ?? 0} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} className="h-9 text-xs" data-testid={testid} />
    </div>
  );
}

export default function SalaryIncome() {
  return <RequireCompany><Inner /></RequireCompany>;
}
